import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Typography, Paper, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

const AdminConsentCallback = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState("processing");
    const [countdown, setCountdown] = useState(5);

    const adminConsent = searchParams.get("admin_consent");
    const tenant = searchParams.get("tenant");
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    useEffect(() => {
        if (error) {
            setStatus("error");
        } else if (adminConsent === "True" || adminConsent === "true") {
            setStatus("success");
        } else {
            setStatus("error");
        }
    }, [adminConsent, error]);

    useEffect(() => {
        if (status === "success") {
            const timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        handleClose();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [status]);

    const handleClose = () => {
        window.close();
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                p: 2,
            }}
        >
            <Paper
                elevation={6}
                sx={{
                    maxWidth: 500,
                    width: "100%",
                    p: 4,
                    borderRadius: 4,
                    textAlign: "center",
                }}
            >
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 3,
                        background: status === "success"
                            ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                            : "linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)",
                    }}
                >
                    {status === "success" ? (
                        <CheckCircleIcon sx={{ fontSize: 48, color: "white" }} />
                    ) : (
                        <ErrorIcon sx={{ fontSize: 48, color: "white" }} />
                    )}
                </Box>

                <Typography variant="h4" gutterBottom>
                    {status === "success" ? "Consent Granted Successfully" : "Consent Failed"}
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {status === "success"
                        ? "Admin consent has been granted for your organisation. The Teams App can now send notifications to your users."
                        : errorDescription || "The admin consent request was not completed. Please try again or contact support if the issue persists."}
                </Typography>

                {status === "success" && tenant && (
                    <Box
                        sx={{
                            bgcolor: "grey.100",
                            borderRadius: 2,
                            p: 2,
                            mb: 3,
                            textAlign: "left",
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Permissions granted:
                        </Typography>
                        <List dense disablePadding>
                            <ListItem disableGutters sx={{ py: 0.25 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                    <FiberManualRecordIcon sx={{ fontSize: 8, color: "primary.main" }} />
                                </ListItemIcon>
                                <ListItemText primary="Send notifications to users" primaryTypographyProps={{ variant: "body2" }} />
                            </ListItem>
                            <ListItem disableGutters sx={{ py: 0.25 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                    <FiberManualRecordIcon sx={{ fontSize: 8, color: "primary.main" }} />
                                </ListItemIcon>
                                <ListItemText primary="Access basic user profile information" primaryTypographyProps={{ variant: "body2" }} />
                            </ListItem>
                            <ListItem disableGutters sx={{ py: 0.25 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                    <FiberManualRecordIcon sx={{ fontSize: 8, color: "primary.main" }} />
                                </ListItemIcon>
                                <ListItemText primary="Read organisation information" primaryTypographyProps={{ variant: "body2" }} />
                            </ListItem>
                        </List>
                    </Box>
                )}

                {status === "error" && error && (
                    <Box
                        sx={{
                            bgcolor: "error.lighter",
                            borderRadius: 2,
                            p: 2,
                            mb: 3,
                            textAlign: "left",
                        }}
                    >
                        <Typography variant="body2">
                            <strong>Error:</strong> {error}
                        </Typography>
                    </Box>
                )}

                <Button
                    variant="contained"
                    onClick={handleClose}
                    sx={{
                        background: "linear-gradient(135deg, #6264A7 0%, #464775 100%)",
                        px: 4,
                        py: 1.5,
                    }}
                >
                    Close Window
                </Button>

                {status === "success" && countdown > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 2, color: "text.secondary" }}>
                        This window will close automatically in {countdown} seconds...
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

export default AdminConsentCallback;
